import { useEffect, useState, type ReactNode } from 'react';
import { fDate, dateOf } from '@nandanam/domain';
import { useStore } from './app/store';
import { Icon, Logo } from './ui/icons';
import { SyncBar, SaveChip, Toast } from './ui/components';
import { Dashboard } from './pages/Dashboard';
import { Overlays } from './overlays/Overlays';
import { PAGES } from './pages/registry';
import type { RouteName } from './app/route';

function useWidth() {
  const [w, setW] = useState(innerWidth);
  useEffect(() => { const f = () => setW(innerWidth); addEventListener('resize', f); return () => removeEventListener('resize', f); }, []);
  return w;
}

export const MOBILE_MAX = 640;

export function App() {
  const S = useStore();
  const w = useWidth();
  const mobile = w <= MOBILE_MAX;
  useEffect(() => { document.documentElement.classList.toggle('is-mobile', mobile); }, [mobile]);
  useEffect(() => { document.querySelector('.main')?.scrollTo(0, 0); }, [S.route.name, JSON.stringify(S.route.params)]);
  if (mobile) return <MobileApp />;
  const narrow = w < 760;
  const route = S.route.name;
  let active: RouteName = route;
  if (route === 'booking') { const r = S.rec(S.route.params.id ?? ''); active = r && ['enquiry', 'hold', 'expired', 'lost'].includes(S.phase(r)) ? 'enquiries' : 'bookings'; }
  if (route === 'customer') active = 'customers';
  if (route === 'expense') active = 'expenses';
  const openEnq = S.state.records.filter(r => ['enquiry', 'hold'].includes(S.phase(r))).length;
  const item = (key: RouteName, label: string, icon: string, count?: number) => (
    <button key={key} className={`nav-item ${active === key ? 'on' : ''}`} title={label} onClick={() => S.nav(key)}>
      <Icon name={icon} size={17} />{!narrow && <><span className="label">{label}</span>{count ? <span className="nav-count num">{count}</span> : null}</>}
    </button>
  );
  const Page = PAGES[route] ?? Dashboard;
  return (
    <div className={`shell ${narrow ? 'narrow' : ''}`}>
      <aside className="sidebar no-print">
        <button className="brand" onClick={() => S.nav('dashboard')}>
          <Logo />
          {!narrow && <span className="brand-name"><b>NANDANAM</b><span>FARMS</span></span>}
        </button>
        <button className="side-cta" title="New enquiry" disabled={!S.canEdit && S.engine.status !== 'offline'} onClick={() => S.open('new', {})}><Icon name="plus" size={16} />{!narrow && 'New enquiry'}</button>
        <nav className="nav">
          {item('dashboard', 'Today', 'home')}{item('calendar', 'Calendar', 'calendar')}{item('enquiries', 'Enquiries', 'inbox', openEnq)}{item('bookings', 'Bookings', 'book')}{item('customers', 'Customers', 'users')}
          <div className="nav-divider" />
          {item('expenses', 'Expenses', 'receipt')}{item('reports', 'Reports', 'chart')}{item('stats', 'Stats', 'rupee')}{item('about', 'About us', 'info')}{item('settings', 'Settings', 'sliders')}
        </nav>
        <div className="side-foot">
          <button className="nav-item" onClick={() => S.nav('account')} title="Account & sync">
            <span className="avatar">{S.account.name[0]}</span>
            {!narrow && <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.25, minWidth: 0 }}><span className="ellipsis" style={{ color: '#F5F5DC', fontWeight: 600 }}>{S.account.name}</span><span style={{ fontSize: 11.5 }}>{fDate(dateOf(S.now), { year: true })} · <SyncWord /></span></span>}
          </button>
        </div>
      </aside>
      <main className="main">
        <SyncBar />
        <div className="save-chip-wrap"><SaveChip /></div>
        <Page />
      </main>
      <Overlays />
      <Toast />
    </div>
  );
}

function SyncWord() {
  const S = useStore(), st = S.engine.status;
  const w: Record<string, string> = { loading: 'Loading', saving: 'Saving', saved: 'Synced', pending: 'Not synced', offline: 'Offline', error: 'Sync error', denied: 'No access' };
  return <>{S.mode === 'demo' ? 'Demo' : w[st]}</>;
}

// Mobile shell is implemented in ./mobile/MobileApp; imported lazily to keep this file focused.
import { MobileApp } from './mobile/MobileApp';
export type { ReactNode };
